import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatEnderecoCompleto } from '@dts/shared';
import { FormPage } from '../../shared/components/FormPage';
import { useCrudFormDialog } from '../../shared/api/useCrudFormDialog';
import { stripDocumentoMask } from '../../shared/utils/document';
import { clientsHooks } from './hooks';
import { clientInputSchema, type ClientFormValues } from './schemas';
import type { ClientDto, ClientInput } from './types';
import { useClientAddressesHooks } from './addresses/hooks';
import { ClientAddressesDialog } from './addresses/ClientAddressesDialog';

export interface ClientFormDialogProps {
  open: boolean;
  client: ClientDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: ClientFormValues = {
  nome: '',
  tipoPessoa: 'PJ',
  documento: '',
  telefone: '',
  contato: '',
  // Campo `endereco` (string legado) preservado no payload mas oculto na UI.
  endereco: null,
  enderecoComercialId: null,
  enderecoFaturamentoId: null,
  enderecoCorrespondenciaId: null,
  ativo: true,
};

export function ClientFormDialog({ open, client, onClose }: ClientFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    ClientFormValues,
    ClientDto,
    ClientInput,
    number
  >({
    open,
    entity: client,
    schema: clientInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (c) => ({
      nome: c.nome,
      tipoPessoa: c.tipoPessoa,
      documento: c.documento,
      telefone: c.telefone,
      contato: c.contato,
      // Preserva valor histórico do legado em PUT mesmo sem UI.
      endereco: c.endereco,
      enderecoComercialId: c.enderecoComercialId,
      enderecoFaturamentoId: c.enderecoFaturamentoId,
      enderecoCorrespondenciaId: c.enderecoCorrespondenciaId,
      ativo: c.ativo,
    }),
    toInput: (v) => ({
      ...v,
      documento: stripDocumentoMask(v.documento),
    }),
    getId: (c) => c.id,
    hooks: { useCreate: clientsHooks.useCreate, useUpdate: clientsHooks.useUpdate },
    messages: {
      created: t('features.clientes.toasts.created'),
      updated: t('features.clientes.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  const tipoPessoa = useWatch({ control: form.control, name: 'tipoPessoa' });

  const clientId = client?.id;
  const addressHooks = useClientAddressesHooks(clientId ?? 0);
  const addressListQuery = addressHooks.useList({ page: 1, pageSize: 100 });
  const availableAddresses = useMemo(
    () => (clientId ? (addressListQuery.data?.data ?? []) : []),
    [clientId, addressListQuery.data],
  );
  const [addressesDialogOpen, setAddressesDialogOpen] = useState(false);

  return (
    <FormPage
      title={isEdit ? t('features.clientes.editTitle') : t('features.clientes.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
    >
      <Stack spacing={2}>
        <TextField
          label={t('features.clientes.fields.nome')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.nome)}
          helperText={form.formState.errors.nome?.message}
          {...form.register('nome')}
        />

        <Controller
          control={form.control}
          name="tipoPessoa"
          render={({ field }) => (
            <FormControl>
              <FormLabel>{t('features.clientes.fields.tipoPessoa')}</FormLabel>
              <RadioGroup
                row
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                onBlur={field.onBlur}
              >
                <FormControlLabel
                  value="PJ"
                  control={<Radio />}
                  label={t('features.clientes.tipoPessoa.PJ')}
                />
                <FormControlLabel
                  value="PF"
                  control={<Radio />}
                  label={t('features.clientes.tipoPessoa.PF')}
                />
              </RadioGroup>
            </FormControl>
          )}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={
              tipoPessoa === 'PF'
                ? t('features.clientes.fields.documentoCpf')
                : t('features.clientes.fields.documentoCnpj')
            }
            fullWidth
            inputProps={{ maxLength: tipoPessoa === 'PF' ? 14 : 18 }}
            error={Boolean(form.formState.errors.documento)}
            helperText={
              form.formState.errors.documento?.message ??
              (tipoPessoa === 'PF'
                ? t('features.clientes.fields.documentoCpfHelper')
                : t('features.clientes.fields.documentoCnpjHelper'))
            }
            {...form.register('documento')}
          />
          <TextField
            label={t('features.clientes.fields.telefone')}
            fullWidth
            inputProps={{ maxLength: 20 }}
            error={Boolean(form.formState.errors.telefone)}
            helperText={form.formState.errors.telefone?.message}
            {...form.register('telefone')}
          />
        </Stack>

        <TextField
          label={t('features.clientes.fields.contato')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.contato)}
          helperText={form.formState.errors.contato?.message}
          {...form.register('contato')}
        />

        <Divider textAlign="left" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('features.clientes.sections.enderecos')}
          </Typography>
        </Divider>

        {!clientId ? (
          <Alert severity="info" variant="outlined">
            {t('features.clientes.sections.enderecosHintCreate')}
          </Alert>
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {t('features.clientes.sections.enderecosHint')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HomeWorkIcon />}
                onClick={() => setAddressesDialogOpen(true)}
              >
                {t('features.clientes.actions.manageAddresses')}
              </Button>
            </Stack>

            {(
              ['enderecoComercialId', 'enderecoFaturamentoId', 'enderecoCorrespondenciaId'] as const
            ).map((name) => (
              <Controller
                key={name}
                control={form.control}
                name={name}
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    label={t(`features.clientes.fields.${name}`)}
                    helperText={fieldState.error?.message}
                    error={Boolean(fieldState.error)}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : Number(e.target.value))
                    }
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    fullWidth
                  >
                    <MenuItem value="">{t('features.clientes.fields.enderecoSlotNone')}</MenuItem>
                    {availableAddresses.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {formatEnderecoCompleto({
                          logradouro: a.logradouro,
                          numero: a.numero,
                          complemento: a.complemento,
                          bairro: a.bairro,
                          cidade: a.cidade,
                          uf: a.uf,
                          cep: a.cep,
                        })}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            ))}

            <ClientAddressesDialog
              open={addressesDialogOpen}
              clientId={clientId}
              clientName={client?.nome}
              onClose={() => setAddressesDialogOpen(false)}
            />
          </>
        )}

        <Divider sx={{ mt: 1 }} />

        <Controller
          control={form.control}
          name="ativo"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? true}
                  onChange={(_, c) => field.onChange(c)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.clientes.fields.ativo')}
            />
          )}
        />
      </Stack>
    </FormPage>
  );
}
